import {IMetaDef} from "/@back@/edit/wed/features/metaBar.js";
import {TxtSelMgr} from "/@back@/edit/wed/features/txtSel.js";
import {IOnTyping, ITxtElement, ITxtRefresh, TxtColModel, TxtModel, TxtOLModel, TxtRoot, TxtRootStr, TxtSLModel, TxtULModel} from "/@back@/edit/wed/wedlets/txt/txt.js";
import {TXTTABLE} from "/@back@/edit/wed/wedlets/txt/txtTable.js";
import {InlElement, InlWrap, TxtBlock, TxtCaption, TxtCell, TxtCol, TxtElement, TxtParaLike, TxtParaParent, TxtRow, TxtSL, TxtStr, TxtTable} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {IElementWedlet, IS_EltWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IWedletSingleElt, WEDLET_SINGLEELT} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM, INodeFilter} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IJmlObj, IJmlSet, JML} from "/@lib@/commons/xml/jml.js";
import {IXAddr, IXAddrRange, XA} from "/@lib@/commons/xml/xAddr.js";
import {IDocBatch} from "/@lib@/edit/docHolder.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {ACTION} from "/@lib@/commons/actions.js";
import {hintsMenu} from "/@back@/edit/wed/wedlets/txt/txtActions.js";


/**
 * Caractère présent en limite d'un noeud texte (espace largeur 0) pour contourner les comportements inadaptés
 * du contentEditable de Chrome.
 */
export const BOUND_CHAR = "\u200B";
export const BOUND_CHARS = "\u200B\u200B";


export const IS_TxtElement = function (elt: any): elt is TxtElement {return elt instanceof TxtElement || elt instanceof TxtCell} as INodeFilter<TxtElement>;
export const IS_TxtBlock = function (elt: any): elt is TxtBlock {return elt instanceof TxtBlock || elt instanceof TxtCell} as INodeFilter<TxtBlock>;
export const IS_TxtParaParent = function (elt: any): elt is TxtParaParent {return elt instanceof TxtParaParent || elt instanceof TxtCell} as INodeFilter<TxtParaParent>;

export const IS_TxtElementReal = function (n: Node): n is TxtElement {return IS_TxtElement(n) && !n.isVirtual()} as INodeFilter<TxtElement>;
export const IS_TxtStr = function (n: Node): n is TxtStr {return n instanceof TxtStr} as INodeFilter<TxtStr>;
export const IS_TxtStrReal = function (n: Node): n is TxtStr {return n instanceof TxtStr && !n.isVirtual()} as INodeFilter<TxtStr>;
export const IS_TxtRoot = function (n: Node): n is TxtRoot {return n instanceof TxtRoot} as INodeFilter<TxtRoot>;


/**
 * Implémentable par les widgets weds pour ajuster la position du caret.
 * Appelé lorsque la sélection est collapsée sur le 1er ancêtre du anchorNode
 * à chaque changement de la selection.
 */
export interface IFixCaret {
	/** @return true si le caret a été déplacé. */
	fixupCaret(selMgr: TxtSelMgr, oldFocus: Node, oldOffset: number): boolean;
}

/**
 * Implémentable par les widgets weds pour ajuster la sélection courante.
 * Appelé lorsque la sélection n'est pas collapsée sur les 1ers ancêtres du anchorNode et du focusNode
 * à chaque changement de la selection.
 */
export interface IFixSelection {
	/**
	 * @param what : 1 = anchor, 2 = focus, 3 = both.
	 * @return true si la sel a été modifiée.
	 */
	fixupSelection(selMgr: TxtSelMgr, what: number, oldFocus: Node, oldOffset: number): boolean;
}

/** Elément enveloppant des des TxtElement qui doit être traversé. */
export interface ITxtWrapElt extends HTMLElement {
	isTxtWrapElt: true
}

export function isTxtWrapElt(n: Node): n is ITxtWrapElt {return (n as ITxtWrapElt).isTxtWrapElt}


/**
 * PROJET : pour injection de balisage extra-schéma dans le text (find, diff...), mémoire
 * indiquant que ce noeud ne possède pas de limite (BOUND_CHARS) explicite en début ou en fin.
 */
export interface ITxtTextNode extends Text {
	startEdgeOmitted?: boolean
	endEdgeOmitted?: boolean
}

/**
 *
 */
export interface ITxtColWidthMgr {
	refreshW(col: TxtCol): void

	/** Valeur logique (unité sémantique) de la largeur. */
	getLogicW(col: TxtCol): number

	/** Bornes de valeur logique (unité sémantique) de la largeur. */
	getLogicBounds(col: TxtCol): { min?: number, max?: number, step?: number }

	/** Modification de la valeur logique (unité sémantique) de la largeur. */
	updateLogicW(col: TxtCol, width: number, batch: IDocBatch): void

	/** Spécifie une valeur par défaut. */
	fillDefault(colMode: TxtColModel, jml: IJmlObj): void

	// Pour resize par drag and drop.
	//updatePhysicW(col: TxtCol, widthInPx: number, batch: IDocBatch): void
}

/**
 *
 */
export interface ITxtCellSpanMgr {
	refreshSpan(cell: TxtCell): void

	isRowSpanAvailable(cell: TxtCell): boolean

	isColSpanAvailable(cell: TxtCell): boolean

	/** Modification du rowSpan via un batch. */
	updateRowSpan?(cell: TxtCell, span: number, batch: IDocBatch): void

	/** Modification du colSpan via un batch. */
	updateColSpan?(cell: TxtCell, span: number, batch: IDocBatch): void

	/** Retourne une nouvelle cell résultat d'une fragmentation d'une cellule. */
	splitCell?(cell: TxtCell, rowSpan: number, colSpan: number): IJmlSet
}

export const cellSpanMgrDefault: ITxtCellSpanMgr = {
	refreshSpan() {},
	isRowSpanAvailable() {return false},
	isColSpanAvailable() {return false}
};

/**
 * Recherche d'un noeud text (virtuel ou non) fils.
 * Impl partagée pour TxtParaLike et InlElement, ie les container d'inline.
 */
export function findOrCreateTxtStr_inline(this: TxtElement, last: boolean): TxtStr {
	let ch = last ? findTxtEltLastChild(this) : findTxtEltFirstChild(this);
	if (!ch) {
		console.trace("CreateOnTheFly: display content");
		this.onChildWedletsChange();
		ch = last ? findTxtEltLastChild(this) : findTxtEltFirstChild(this);
	}
	if (!(ch instanceof TxtStr)) {
		//Injection d'un noeud texte virtuel.
		return (ch as InlElement).findOrCreateCaretableSibling(!last);
	}
	return ch;
}

/** Fusion entre 2 noeuds frères. */
export function merge_none(this: TxtElement, next: TxtElement, batch: IDocBatch): boolean {return false}

export function merge_elts(this: TxtElement, next: TxtElement, batch: IDocBatch): boolean {
	if (!next || !this.isMergeable(next)) return false;
	if (next.isVirtual()) {
		if (next.mustKeepAsVirtual()) {
			//next est virtual
			const parent = findTxtEltParent(next);
			const toDel = parent ? parent.onChildDeletedByCaret(next) : null;
			if (toDel) {
				//On supprime le container complet qui est vide (liste entière...)
				batch.deleteSequence(toDel.wedAnchor, 1);
				return true;
			}
			this.setCaretIn(this.txtRoot.selMgr, false);
			return false;
		}
		this.setCaretIn(this.txtRoot.selMgr, false);
		next.remove();
		return true;
	}
	//console.log("contentToMerge::::", content);
	const parent = findTxtEltParent(next);
	const toDel = parent ? parent.onChildDeletedByCaret(next) : null;
	batch.deleteSequence((toDel || next).wedAnchor, 1);
	const content = next.exportContent();
	if (this.isVirtual()) {
		WEDLET.insertDatasFromDisplay(this, batch, content);
		this.setCaretIn(this.txtRoot.selMgr, true);
		//batch.setSelAfter(XA.append(this.wedAnchor, 0));
	} else {
		if (JML.isText(content[0])) {
			//le contenu à déplacer commence par un texte,
			const last = findTxtEltLastChild(this);
			if (last instanceof TxtStr && !last.isVirtual()) {
				// this se termine par un texte, on va fusionner les textes.
				const txtXa = XA.append(last.wedAnchor, last.getXmlTextLength());
				batch.insertText(txtXa, content[0] as string);
				content.splice(0, 1);
				//batch.setSelAfter(txtXa);
			}
		}
		if (content.length > 0) batch.insertJml(XA.append(this.wedAnchor, getLastChildXmlOffset(this) + 1), content);
		const lastTxt = this.findOrCreateTxtStr(true);
		lastTxt.setCaretIn(this.txtRoot.selMgr, false);
		//batch.setSelAfter(lastTxt.isVirtual() ? lastTxt.wedAnchor : XA.append(lastTxt.wedAnchor, lastTxt.getXmlTextLength()));
	}
	return true;
}

/** isUnwrappable() pour InlElement, TxtStr, TxtParaLike. */
export function isUnwrappable_inline(this: TxtElement, target?: "list" | "div"): "list" | "div" | null {
	//On remonte au container pour savoir si le container peut-etre unwrappé.
	return findTxtEltParent(this)?.isUnwrappable(target);
}

/** unwrapSeq() pour InlElement, TxtStr, TxtParaLike. On remonte juqu'au para pour unwrapper son container. */
export function unwrapSeq_inline(this: TxtElement, target: "list" | "div" | null, fromChild: TxtElement, toChild: TxtElement, batch: IDocBatch, addSelAfter: boolean): boolean {
	return findTxtEltParent(this)?.unwrapSeq(target, this, this, batch, addSelAfter) || false;
}

export function findTxtEltParent(from: Node | null, includeFrom?: boolean): TxtElement {
	if (from == null) return null;
	if (!includeFrom) {
		from = from.parentNode;
		if (from == null) return null;
	}
	do {
		if (IS_TxtElement(from)) return from;
		if (from instanceof TxtRoot) return null;
	} while (from = from.parentNode);
	return null;
}

export function findTxtEltOrRootParent(from: Node | null, includeFrom?: boolean): TxtElement | TxtRoot {
	if (from == null) return null;
	if (!includeFrom) {
		from = from.parentNode;
		if (from == null) return null;
	}
	do {
		if (IS_TxtElement(from)) return from;
		if (from instanceof TxtRoot) return from;
	} while (from = from.parentNode);
	return null;
}

export function findTxtEltNextSibling(from: TxtElement | ITxtWrapElt): TxtElement {
	let n = from ? from.nextElementSibling : null;
	while (n) {
		if (IS_TxtElement(n)) return n;
		if (isTxtWrapElt(n)) {
			const ch = findTxtEltFirstChild(n);
			if (ch) return ch;
		}
		n = n.nextElementSibling;
	}
	if (from && isTxtWrapElt(from.parentNode)) return findTxtEltNextSibling(from.parentNode);
	return null;
}

export function findTxtEltPreviousSibling(from: TxtElement | ITxtWrapElt): TxtElement {
	let n = from ? from.previousElementSibling : null;
	while (n) {
		if (IS_TxtElement(n)) return n;
		if (isTxtWrapElt(n)) {
			const ch = findTxtEltLastChild(n);
			if (ch) return ch;
		}
		n = n.previousElementSibling;
	}
	if (from && isTxtWrapElt(from.parentNode)) return findTxtEltPreviousSibling(from.parentNode);
	return null;
}

export function findTxtEltFirstChild(from: Element): TxtElement {
	let n = from ? from.firstElementChild : null;
	while (n) {
		if (IS_TxtElement(n)) return n;
		if (isTxtWrapElt(n)) {
			const ch = findTxtEltFirstChild(n);
			if (ch) return ch;
		}
		n = n.nextElementSibling;
	}
	return null;
}

export function findTxtEltLastChild(from: Element): TxtElement {
	let n = from ? from.lastElementChild : null;
	while (n) {
		if (IS_TxtElement(n)) return n;
		if (isTxtWrapElt(n)) {
			const ch = findTxtEltLastChild(n);
			if (ch) return ch;
		}
		n = n.previousElementSibling;
	}
	return null;
}

export function findTxtStrParent(from: Node, includeFrom?: boolean): TxtStr {
	if (!from) return null;
	if (!includeFrom) {
		from = from.parentNode;
		if (!from) return null;
	}
	do {
		if (from instanceof TxtStr) return from;
		if (from instanceof TxtRoot) return null;
	} while (from = from.parentNode);
	return null;
}


export function findTxtStrFirstChild(from: Node): TxtStr {
	return DOM.findNext(from, from, IS_TxtStr);
}

export function findTxtStrLastChild(from: Node): TxtStr {
	return DOM.findPreviousIn(from, IS_TxtStr);
}

export function findTxtStrPrevious(from: Node): TxtStr {
	let node = DOM.findPrevious(from, null);
	while (node) {
		if (node instanceof TxtStr) return node;
		if (node instanceof TxtRoot) return null;
		node = DOM.findPrevious(node, null);
	}
	return null;
}

export function findTxtStrNext(from: Node): TxtStr {
	let node = DOM.findNext(from, null);
	while (node) {
		if (node instanceof TxtStr) return node;
		if (node instanceof TxtRoot) return null;
		node = DOM.findNext(node, null);
	}
	return null;
}


export function findTxtParaLikeFrom(from: Node): TxtParaLike {
	if (!from) return null;
	do {
		if (from instanceof TxtParaLike) return from;
		if (from instanceof TxtRoot) return null;
	} while (from = from.parentNode);
	return null;
}

export function findTxtParaSiblingFrom(from: Node): TxtBlock {
	if (!from) return null;
	do {
		if (IS_TxtElement(from) && from.isParaSibling) return from;
		if (from instanceof TxtRoot) return null;
	} while (from = from.parentNode);
	return null;
}

/**
 * Retourne l'offset du dernier fils.
 * -1 si pas fils et pas de metas,
 * 0 si pas de fils mais des metas,
 * 0 si 1 fils et pas de metas
 */
export function getLastChildXmlOffset(elt: TxtElement): number {
	let ch = findTxtEltLastChild(elt);
	while (ch && ch.isVirtual()) ch = findTxtEltPreviousSibling(ch);
	return ch ? ch.xaPart : elt.getFirstChildXmlOffset() - 1;
}

export function webRange2XaRange(range: StaticRange): IXAddrRange {
	const xaStart = buildXa(range.startContainer, range.startOffset);
	const xaEnd = buildXa(range.endContainer, range.endOffset);
	return {start: xaStart, end: xaEnd}
}

/** Xa d'un point d'insertion, en remontant au contexte réel en cas de point virtuel. */
export function buildXa(from: Node, webOffset?: number): IXAddr {
	if (webOffset === undefined) {
		while (!IS_TxtElement(from) && !(from instanceof TxtRoot)) {
			from = from.previousSibling || from.parentNode;
		}
		return from.wedAnchor;
	}
	const txtFrom = findTxtStrParent(from, true);
	if (txtFrom === null) {
		let target = from.childNodes[webOffset];
		if (IS_TxtElement(target)) return target.wedAnchor;
		target = from.childNodes[webOffset - 1];
		if (IS_TxtElement(target)) return target.isVirtual() ? target.wedAnchor : XA.incrAtDepth(target.wedAnchor, -1, 1);
		if (IS_TxtElement(from)) return from.wedAnchor; //on pointe un txt-object par ex.
		return null;
	}
	if (txtFrom.isVirtual()) {
		let virtualRoot: TxtElement = txtFrom;
		let parent = findTxtEltParent(virtualRoot);
		while (parent && parent.isVirtual()) {
			virtualRoot = parent;
			parent = findTxtEltParent(virtualRoot);
		}
		return virtualRoot.wedAnchor;
	}
	return XA.append(txtFrom.wedAnchor, txtFrom.getXmlOffset(from, webOffset));
}

/** Xa d'un point d'insertion, éventuellement en contexte virtuel. */
export function buildXaInVirtual(from: Node, webOffset: number): IXAddr {
	const txtFrom = findTxtStrParent(from, true);
	return txtFrom.isVirtual() ? XA.append(txtFrom.wedAnchor, 0) : XA.append(txtFrom.wedAnchor, txtFrom.getXmlOffset(from, webOffset));
}

export function isStaticRangeCollapsed(r: StaticRange) {return r.startContainer === r.endContainer && r.startOffset === r.endOffset}

// export function getAncestor(rg: StaticRange|Range): Node{
// 	if(rg instanceof Range) return rg.commonAncestorContainer;
// 	let r = new Range();
// 	r.setStart(rg.startContainer, rg.startOffset);
// 	r.setStart(rg.endContainer, rg.endOffset);
// 	return r.commonAncestorContainer;
// }

// export function createStaticRangeAround(node: Node): StaticRange {
// 	let offs = DOM.computeOffset(node, 0);
// 	return {startContainer: node.parentNode, startOffset: offs, endContainer: node.parentNode, endOffset: offs + 1}
// }

export function adjustVirtualsPara(this: void, parent: ITxtElement) {
	//noeud admettant des paragraphes.
	//on injecte les noeuds paragraphes virtuels partout où ils manquent (début, fin et entre 2 bocs non para).
	let needPara = true;
	let ch = findTxtEltFirstChild(parent);
	while (ch) {
		if (ch instanceof TxtParaLike) {
			if (!needPara) {
				//le précédent est déjà un para
				if (ch.isVirtual() && !ch.isRegularDisplay) {
					//1 noeud virtuel après un para, on vire.
					const prev = ch;
					ch = findTxtEltNextSibling(ch);
					prev.remove();
					continue;
				}
			} else {
				needPara = false;
			}
		} else if (needPara) {
			parent.insertVirtualPara(ch);
		} else {
			needPara = true;
		}
		ch = findTxtEltNextSibling(ch);
	}
	if (needPara) parent.insertVirtualPara(null);
}

export function adjustVirtualsStr(this: void, parent: ITxtElement, tailNode?: Node) {
	//noeud admettant des noeuds textes.
	//on injecte les noeuds textes virtuels partout où ils manquent.
	let needTxtStr = true;
	let ch = findTxtEltFirstChild(parent);
	while (ch && (ch !== tailNode)) {
		if (ch instanceof TxtStr) {
			if (!needTxtStr) {
				//le précédent est déjà un txtStr
				if (ch.isVirtual()) {
					//1 txtStr virtuel après un txtStr, on vire.
					const prev = ch;
					ch = findTxtEltNextSibling(ch);
					prev.remove();
					continue;
				}
			} else {
				needTxtStr = false;
			}
		} else if (needTxtStr) {
			parent.insertVirtualStr(ch);
		} else {
			needTxtStr = true;
		}
		ch = findTxtEltNextSibling(ch);
	}
	if (needTxtStr) parent.insertVirtualStr(tailNode);
}

export function createChildToDisplay(parent: IElementWedlet, childModel: TxtModel, insertBefore?: Node) {
	const child = childModel.createWedlet(parent.wedlet);
	(child as IWedletSingleElt).insertElement(parent.delegatedHost || parent, insertBefore);
	child.bindAsVirtual();
}

export function deleteContent(ctn: TxtElement, batch: IDocBatch) {
	const first = ctn.getFirstChildXmlOffset();
	const last = getLastChildXmlOffset(ctn);
	if (first <= last) batch.deleteSequence(XA.append(ctn.wedAnchor, first), last - first + 1);
}

/**
 * Supprime le contenu à partir d'un range de noeuds TxtElement.
 * @return true si le stx-str du startPoint a été supprimé.
 */
export function deleteWebRange(txtRoot: TxtRoot, webRange: Range, batch: IDocBatch, backward?: boolean, absorbCtn?: boolean, willInsert?: boolean): boolean {
	//Bloquage des suppr des cell / row des tables.
	let wr = webRange;
	const anc = wr.commonAncestorContainer;
	let txtStrNodeDeleted = false;
	//Exclusion des structures de table sur le début du range.
	let ctn = wr.startContainer;

	let firstCellInEndSelFound: boolean;

	function moveRg(ctn: TxtElement, before?: boolean) {
		if (wr === webRange) {
			//1er move, on clone webRange avant modif.
			if (!before && backward) batch.setSelAfter(buildXa(wr.startContainer, wr.startOffset)); //si backard et débute de sel, on se placere en début de sel.
			wr = webRange.cloneRange();
		}
		if (!firstCellInEndSelFound && before && !backward) {
			//si !backard et fin de sel, on se placer en fin de sel.
			firstCellInEndSelFound = true;
			batch.setSelAfter(ctn.wedAnchor);
		}
		if (before) wr.setEnd(ctn.parentNode, DOM.computeOffset(ctn));
		else wr.setStart(ctn.parentNode, DOM.computeOffset(ctn) + 1);
	}

	while (ctn !== anc) {
		if (ctn instanceof TxtCell || ctn instanceof TxtCaption) {
			//supression partielle du contenu de la cell ou du caption.
			const subRg = wr.cloneRange();
			subRg.setEnd(ctn, DOM.computeOffset(ctn.lastChild, -1) + 1);
			txtStrNodeDeleted = deleteWebRange(txtRoot, subRg, batch, backward, absorbCtn);
			if (ctn instanceof TxtCaption) batch.needAdjustForNextAdds(); //Le caption a pu être intégralement supprimé => ajustement nécessaire pour suppr suivantes dans les rows.
			//on déplace le début du webRange après notre cell / caption
			moveRg(ctn as any as TxtElement);
		} else if (ctn instanceof TxtRow) {
			//On suppr le contenu des cells suivantes
			for (let cell = ctn.childNodes.item(wr.startOffset) as Node; cell; cell = cell.nextSibling) {
				if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
			}
			//on déplace le début du webRange après notre row
			moveRg(ctn);
		} else if (ctn instanceof HTMLTableElement) {
			//On suppr le contenu des cells des rows suivantes
			for (let row = ctn.childNodes.item(wr.startOffset) as Node; row; row = row.nextSibling) {
				if (row instanceof TxtRow) {
					for (let cell = row.firstChild; cell; cell = cell.nextSibling) {
						if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
					}
				} else if (row instanceof TxtCaption) {
					deleteContent(row, batch); //TODO suppr caption si optionnel ?
				}
			}
			//on déplace le début du webRange après notre table
			moveRg(ctn.parentElement as TxtTable);
		}
		ctn = ctn.parentNode;
	}

	//Exclusion des structures de table sur la fin du range.
	ctn = wr.endContainer;
	while (ctn !== anc) {
		if (ctn instanceof TxtCell || ctn instanceof TxtCaption) {
			//supression partielle du contenu de la cell ou du caption.
			const subRg = wr.cloneRange();
			subRg.setStart(ctn, 0);
			deleteWebRange(txtRoot, subRg, batch, backward, absorbCtn);
			//on déplace le début du webRange avant notre cell / caption
			moveRg(ctn as any as TxtElement, true);
		} else if (ctn instanceof TxtRow) {
			//On suppr le contenu des cells précédentes
			for (let cell = ctn.childNodes.item(wr.endOffset - 1) as Node; cell; cell = cell.previousSibling) {
				if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
			}
			//on déplace le début du webRange après notre row
			moveRg(ctn, true);
		} else if (ctn instanceof HTMLTableElement) {
			//On suppr le contenu des cells des rows précédentes
			for (let row = ctn.childNodes.item(wr.endOffset - 1) as Node; row; row = row.previousSibling) {
				if (row instanceof TxtRow) {
					for (let cell = row.firstChild; cell; cell = cell.nextSibling) {
						if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
					}
				} else if (row instanceof TxtCaption) {
					deleteContent(row, batch); //TODO suppr caption si optionnel ?
				}
			}
			//on déplace le début du webRange avant notre table
			moveRg(ctn.parentElement as TxtTable, true);
		}
		ctn = ctn.parentNode;
	}

	//Exclusion des structures de table sur l'ancestor du range.
	if (anc instanceof TxtRow) {
		if (wr.startContainer !== anc || wr.endContainer !== anc) throw Error(); //? les 1ers traitement auraient du remonter wr jusqu'à anc.
		for (let cell = anc.childNodes.item(wr.startOffset) as Node, end = anc.childNodes.item(wr.endOffset); cell !== end; cell = cell.nextSibling) {
			if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
		}
		return txtStrNodeDeleted;
	} else if (anc instanceof HTMLTableElement) {
		if (wr.startContainer !== anc || wr.endContainer !== anc) throw Error(); //? les 1ers traitement auraient du remonter wr jusqu'à anc.
		for (let row = ctn.childNodes.item(wr.startOffset) as Node, end = anc.childNodes.item(wr.endOffset); row !== end; row = row.nextSibling) {
			if (row instanceof TxtRow) {
				for (let cell = row.firstChild; cell; cell = cell.nextSibling) {
					if (cell instanceof TxtCell) deleteContent(cell as any as TxtElement, batch);
				}
			} else if (row instanceof TxtCaption) {
				deleteContent(row, batch); //TODO suppr caption si optionnel ?
			}
		}
		return txtStrNodeDeleted;
	}

	//Suppr du contenu restant (hors table)
	if (!willInsert) wr = absorbSpacesForDel(wr);
	let xaRange = webRange2XaRange(wr);
	//Suppr des noeuds textes vidés aux extrémités du range.
	const start = findTxtStrParent(wr.startContainer);
	const end = findTxtStrParent(wr.endContainer);
	let endEated = false;
	if (end) {
		if (end.isVirtual()) {
			endEated = true;
		} else if (start !== end) {
			//Le cas où start === end est traité après.
			if (end.isXmlEndOffset(wr.endContainer, wr.endOffset)) {
				//on se place après le noeud texte de fin.
				endEated = true;
				xaRange.end = XA.incrAtDepth(XA.up(xaRange.end), -1, 1);
			} else {
				//fin de la sel au milieu d'un texte, aucune absorption.
				absorbCtn = false;
			}
		}
	}
	if (absorbCtn) {
		//ATTENTION Pas de la suprression du noeud texte vide seul car pb avec <txt-root-inline> et ctrl+A + saisie d'un caractère (à cause beforeInputNotCancellable et gestion native focus)
		//On ne supprime le noeud start que si les ctn ancêtres sont aussi supprimés. XXX encore valide ?
		if (start && !start.isVirtual() && start.isXmlStartOffset(wr.startContainer, wr.startOffset)) {
			//start est entièrement supprimé.
			if (start === end) {
				//on est dans le même noeud texte.
				if (end.isXmlEndOffset(wr.endContainer, wr.endOffset)) {
					const xaParent = XA.up(XA.freeze(xaRange.start));
					if (!XA.isAttribute(xaParent)) { //cas TxtStrAlone bindé en attr
						//on suppr le noeud texte.
						const superRg = WEDLET.absorbContainersOnDelete(txtRoot.wedMgr.rootWedlet, XA.newRangeAround(xaParent));
						if (superRg) xaRange = superRg;
						absorbCtn = false; //déjà traité
					}
				}
			} else if (endEated) {
				//end est différent et n'est pas au milieu d'un texte
				const startUp = XA.up(XA.freeze(xaRange.start));
				const superRg = WEDLET.absorbContainersOnDelete(txtRoot.wedMgr.rootWedlet, {start: startUp, end: xaRange.end});
				if (superRg) xaRange = superRg;
				absorbCtn = false; //déjà traité
			}
		}
	}
	batch.deleteRange(absorbCtn ? WEDLET.absorbContainersOnDelete(txtRoot.wedMgr.rootWedlet, xaRange) : xaRange);
	return txtStrNodeDeleted;
}

/** Augmente la sel pour qu'un suppr n'amène pas à 2 espaces successifs.// */
function absorbSpacesForDel(rg: Range): Range {
	const start = rg.startContainer;
	if (start instanceof Text && /\s/.test(start.nodeValue.charAt(rg.startOffset - 1))) {
		//Espace avant la sel
		const end = rg.endContainer;
		while (end instanceof Text && /\s/.test(end.nodeValue.charAt(rg.endOffset))) {
			//Espace après la sel
			//rg = rg.cloneRange();
			rg.setEnd(rg.endContainer, rg.endOffset + 1);
		}
	}
	return rg;
}

/**
 * Export JML d'un fragment entre 2 noeuds.
 * startCtn DOIT précéder (ou être égal) à endCtn dans l'odre naturel des noeuds.
 * ATTENTION : docHolder synchrone (=> pour actions édition uniquement).
 */
export function exportFragment(startCtn: TxtElement, xmlStartOffset: number, endCtn: TxtElement, xmlEndOffset: number): IJmlSet {
	const docHolder = startCtn.wedMgr.docHolder;
	let result: IJmlSet = [];

	function copyContent(elt: TxtElement) {
		if (elt instanceof TxtStr && elt.isEmpty()) return;
		Array.prototype.push.apply(result, docHolder.getContent(elt.wedAnchor));
	}

	function copySiblings(sibling: Element): boolean {
		while (sibling) {
			if (IS_TxtElement(sibling)) {
				if (sibling === endCtn) {
					if (!sibling.isVirtual()) {
						if (endCtn instanceof TxtStr) {
							if (xmlEndOffset > 0) result.push(endCtn.getXmlText().substring(0, xmlEndOffset));
						} else {
							result = endCtn.exportWrap(result);
							let ch = findTxtEltFirstChild(endCtn);
							while (ch) {
								if (!ch.isVirtual()) {
									if (ch.xaPart as number >= xmlEndOffset) break;
									copyContent(ch);
								}
								ch = findTxtEltNextSibling(ch);
							}
						}
					}
					return true;
				} else {
					if (!sibling.isVirtual()) copyContent(sibling);
				}
			}
			sibling = sibling.nextElementSibling;
		}
		return false;
	}

	if (startCtn === endCtn) {
		if (startCtn instanceof TxtStr) {
			if (xmlStartOffset < xmlEndOffset) result.push(startCtn.getXmlText().substring(xmlStartOffset, xmlEndOffset));
		} else {
			let ch = startCtn.findTxtChild(xmlStartOffset);
			while (ch) {
				if (!ch.isVirtual()) {
					if (ch.xaPart as number >= xmlEndOffset) break;
					copyContent(ch);
				}
				ch = findTxtEltNextSibling(ch);
			}
		}
	} else {
		if (startCtn instanceof TxtStr) {
			if (startCtn.getXmlTextLength() > xmlStartOffset) result.push(startCtn.getXmlText().substring(xmlStartOffset));
			if (copySiblings(startCtn.nextElementSibling)) return result;
		} else {
			if (copySiblings(startCtn.findTxtChild(xmlStartOffset))) return result;
		}

		//endCtn pas trouvé, on remonte les containers.
		let parent = startCtn.parentElement;
		while (parent && parent !== endCtn) {
			if (IS_TxtElement(parent)) {
				result = parent.exportWrap(result);
			}
			if (copySiblings(parent.nextElementSibling)) return result;
			parent = parent.parentElement;
		}
	}
	return result;
}

/** Supprime la sélection ou le contenu à partir du caret. */
export function deleteFromSel(txtRoot: TxtRoot, forward: boolean, granularity: 'word' | 'character') {
	const selMgr = txtRoot.selMgr;
	let batch: IDocBatch;
	const selType = selMgr.type;
	//let needFreezeFocus = false;
	const wedMgr = txtRoot.wedMgr;
	if (selType === 'Caret') {
		const focusNode = selMgr.focusNode;
		const txtStr = findTxtStrParent(focusNode);
		batch = wedMgr.docHolder.newBatch();
		if (forward ? !txtStr.isXmlEndOffset(focusNode, selMgr.focusOffset) : !txtStr.isXmlStartOffset(focusNode, selMgr.focusOffset)) {
			//suppr dans un noeud texte.
			txtStr.moveCaretFrom(selMgr, !forward, 'extend', granularity);
			deleteWebRange(txtRoot, selMgr.range, batch, !forward, true);
			batch.doBatch();
			if (!txtRoot.isConnected) WEDLET_SINGLEELT.reselectOnVirtualizing(txtRoot);
			//PAS de batch.setSelAfter() : optimisation pour suppr de chaque car + merge de ces micro msgs.
			return;
		} else {
			txtStr.deleteByCaret(true, !forward, batch);
		}
	} else if (selType === 'Range') {
		batch = wedMgr.docHolder.newBatch(selMgr.getXaRange());
		const selAround = selMgr.selAround;
		if (selAround) {
			batch.deleteRange(WEDLET.absorbContainersOnDelete(txtRoot.wedMgr.rootWedlet, XA.newRangeAround(selAround.wedAnchor)));
		} else {
			deleteWebRange(txtRoot, selMgr.range, batch, !forward, true);
		}
		selMgr.collapseToStart();
	} else if (selType === 'Object') {
		batch = wedMgr.docHolder.newBatch(selMgr.getXaRange());
		const range = new Range();
		range.selectNode(selMgr.focusObject);
		deleteWebRange(txtRoot, range, batch, !forward, true);
		txtRoot.moveCaret(true);
	} else {
		return;
	}
	// if (needFreezeFocus) {
	// 	try {
	// 		wedMgr.freezeFocus = true;
	// 		batch.doBatch();
	// 	} finally {
	// 		wedMgr.freezeFocus = false;
	// 	}
	// } else {
	batch.setSelAfterSkipAdjust(selMgr.getXaRange().start);
	batch.doBatch();
	if (!txtRoot.isConnected) WEDLET_SINGLEELT.reselectOnVirtualizing(txtRoot);
	//}
}


export function splitPara(txtRoot: TxtRoot, para: TxtParaLike, splitParent: Node, splitOffset: number, batch: IDocBatch) {
	const endTxtStr = findTxtStrParent(splitParent);
	const lastTxtStr = para.findOrCreateTxtStr(true);
	const contentToMove = exportFragment(endTxtStr, endTxtStr.getXmlOffset(splitParent, splitOffset), lastTxtStr, Number.MAX_SAFE_INTEGER);
	//console.log("contentToMove:::", contentToMove);
	if (para.addNextPara(batch, contentToMove)) {
		deleteWebRange(txtRoot, DOM.newRange(splitParent, splitOffset, para, para.childNodes.length), batch);
	}
}


export function unwrapContainerFromSel(target: "list" | "div" | null, root: TxtRoot, checkAvailable: boolean, addSelAfter?: boolean): boolean {
	const selMgr = checkAvailable ? root.selMgrAsIs : root.selMgr;
	const batch = checkAvailable ? null : root.wedlet.wedMgr.docHolder.newBatch(selMgr.getXaRange());
	if (selMgr.type === 'Range') {
		const range = selMgr.range;
		let ancestor = findTxtEltOrRootParent(range.commonAncestorContainer, true);
		if (ancestor instanceof TxtStr) ancestor = findTxtEltParent(ancestor);
		if (ancestor instanceof TxtRoot) {
			//cas particulier, sel à la racine, on unwrap tous les fils.
			let from = findTxtEltParent(range.startContainer, true);
			while (from && findTxtEltOrRootParent(from) !== ancestor) from = findTxtEltParent(from);
			let to = findTxtEltParent(range.endContainer, true);
			while (to && findTxtEltOrRootParent(to) !== ancestor) to = findTxtEltParent(to);
			to = findTxtEltNextSibling(to);
			for (let ch = from; ch !== to; ch = findTxtEltNextSibling(ch)) {
				if (ch.isUnwrappable(target)) {
					if (checkAvailable) return true;
					ch.unwrapSeq(target, findTxtEltFirstChild(ch), findTxtEltLastChild(ch), batch, addSelAfter);
					batch.needAdjustForNextAdds();
				}
			}
		} else if (ancestor?.isUnwrappable(target)) {
			if (checkAvailable) return true;
			let from = findTxtEltParent(range.startContainer, true);
			while (findTxtEltOrRootParent(from) !== ancestor) from = findTxtEltParent(from);
			let to = findTxtEltParent(range.endContainer, true);
			while (findTxtEltOrRootParent(to) !== ancestor) to = findTxtEltParent(to);
			ancestor.unwrapSeq(target, from, to, batch, addSelAfter);
		}
	} else {
		//console.log("root.activeElement::::::::", root.activeElement);
		const ch = findTxtEltParent(selMgr.focusNode, true);
		if (ch?.isUnwrappable(target)) {
			if (checkAvailable) return true;
			ch.unwrapSeq(target, findTxtEltFirstChild(ch), findTxtEltLastChild(ch), batch, addSelAfter);
		}
	}
	if (checkAvailable || batch.getState() === 'empty') return false;
	batch.doBatch();
	return true;
}


export function unwrapInlFromSel(root: TxtRoot, checkAvailable: boolean): boolean {
	const selMgr = checkAvailable ? root.selMgrAsIs : root.selMgr;
	let ctn = selMgr.commonTxtElement;
	if (ctn instanceof TxtStr) ctn = findTxtEltParent(ctn);
	if (ctn instanceof InlWrap) {
		if (!checkAvailable) {
			const batch = root.wedlet.wedMgr.docHolder.newBatch();
			batch.setSelBeforeSeq(ctn.wedAnchor, 1);
			unwrapInl(ctn, batch);
			const prev = findTxtEltPreviousSibling(ctn);
			if (prev instanceof TxtStr) {
				let offset = prev.getXmlTextLength();
				const rg = selMgr.range;
				const txtStr = findTxtStrParent(rg.startContainer, true);
				if (txtStr) offset += txtStr.getXmlOffset(rg.startContainer, rg.startOffset);
				batch.setSelAfterSkipAdjust(XA.append(prev.wedAnchor, offset));
			} else {
				console.log("prev TxtStr not found???");
				batch.setSelAfterSkipAdjust(ctn.wedAnchor);
			}
			batch.doBatch();
		}
		return true;
	}
	return false;
}

/**
 * Unwrap une balise inline avec mémoire des noeuds textes adjacents supprimés suite à leur fusion.
 * @param remMem Key: noeud supprimé, Value: noeud à utiliser à la place.
 *    Cette mémoire des TxtStr fusionnés, si renseignée, permet d'enchainer plusieurs unwrap.
 */
export function unwrapInl(inl: InlWrap, batch: IDocBatch, remMem?: Map<TxtStr, TxtStr>/*, setSelAfter: boolean*/) {
	const content = inl.exportContent();
	let beforeTxtStr: TxtStr, afterTxtStr: TxtStr;
	let mergeBefore: boolean;
	if (typeof content[0] === 'string') {
		const prev = DOM.findPreviousSibling(inl, IS_TxtElementReal);
		if (IS_TxtStrReal(prev)) {
			mergeBefore = true;
			beforeTxtStr = prev;
		}
	}
	let mergeAfter: boolean;
	if (typeof content[content.length - 1] === 'string') {
		const next = DOM.findNextSibling(inl, IS_TxtElementReal);
		if (IS_TxtStrReal(next)) {
			mergeAfter = true;
			afterTxtStr = next;
		}
	}
	const inlXa = XA.freeze(inl.wedAnchor);
	if (mergeBefore && mergeAfter && content.length === 1) {
		//Cas de fusion complète avec suppr de mergeAfter.
		if (remMem) {
			beforeTxtStr = remMem.get(beforeTxtStr) || beforeTxtStr;
			afterTxtStr = remMem.get(afterTxtStr) || afterTxtStr;
		}
		const txt = content[0] as string + afterTxtStr.getXmlText();
		batch.insertText(XA.append(beforeTxtStr.wedAnchor, beforeTxtStr.getXmlText().length), txt);
		batch.deleteSequence(inl.wedAnchor, 2);//on suppr inl et le noeud texte.
		batch.needAdjustForNextAdds();
		if (remMem) remMem.set(afterTxtStr, beforeTxtStr);
		return;
	} else {
		if (mergeBefore) {
			if (remMem) beforeTxtStr = remMem.get(beforeTxtStr) || beforeTxtStr;
			batch.insertText(XA.append(beforeTxtStr.wedAnchor, beforeTxtStr.getXmlText().length), content[0] as string);
			content.splice(0, 1);
		}
		if (mergeAfter) {
			if (remMem) afterTxtStr = remMem.get(afterTxtStr) || afterTxtStr;
			batch.insertText(XA.append(afterTxtStr.wedAnchor, 0), content[content.length - 1] as string);
			content.length--;
		}
	}
	batch.spliceSequence(inlXa, 1, content).needAdjustForNextAdds();
}


export function onPointerDownObject(this: TxtElement, ev: UIEvent) {
	ev.stopPropagation();
	ev.preventDefault();
	this.focus();
	//console.log("clickObject!!!", this.tabIndex, this.selection.focusNode, this.selection.focusOffset, this.selection.rangeCount, DOMSH.findDocumentOrShadowRoot(this).activeElement);
}

export function hasMetaDefs_multi(this: TxtElement): boolean {
	return this.model.panelModel != null || findTxtEltParent(this)?.hasMetaDefs() || false
}

export function getMetaDefs_multi(this: TxtElement): IMetaDef | IMetaDef[] {
	const result = findTxtEltParent(this)?.getMetaDefs();
	if (!this.model.panelModel) return result;
	const thisMetaDef = {from: this, model: this.model.panelModel, label: this.model.nodeLabel};
	if (!result) return thisMetaDef;
	if (!Array.isArray(result)) return [result, thisMetaDef];
	result.push(thisMetaDef);
	return result;
}

export function getTextFirstOffset(txt: ITxtTextNode): number {
	return txt.startEdgeOmitted ? 0 : 1;
}

export function getTextLength(txt: ITxtTextNode): number {
	return txt.nodeValue.length - (txt.startEdgeOmitted ? txt.endEdgeOmitted ? 0 : 1 : txt.endEdgeOmitted ? 1 : 2);
}

export function doInput(data: string, ev: InputEvent | CompositionEvent, root: TxtRoot) {
	const wedMgr = root.wedlet.wedMgr;
	const selMgr = root.selMgr;
	const range = selMgr.range; //ev.getTargetRanges()[0]; A voir...
	//console.trace("doInput", data, ev, range);
	const batch = wedMgr.docHolder.newBatch();
	let startTxtStrDeleted = false;
	if (!isStaticRangeCollapsed(range)) {
		const xaRange = selMgr.getXaRange();
		batch.setSelBefore(xaRange.start, xaRange.end);
		startTxtStrDeleted = deleteWebRange(root, range, batch, false, false, true);
		batch.setSelAfter(null, null); //reset du selAfter renseigné par deleteWebRange()
	}
	const txtStr = findTxtStrParent(range.startContainer);
	if (!txtStr) ERROR.log("txtStr is null", Error(`range.startContainer::connected:${range.startContainer ? range.startContainer.isConnected : 'null'}::${DOM.debug(range.startContainer)}|parent::${DOM.debug(range.startContainer.parentNode)}`), null);
	if (txtStr.transformInsertData) data = txtStr.transformInsertData(data);
	const xmlOffset = txtStr.getXmlOffset(range.startContainer, range.startOffset);
	let customCaretParent: Element;
	let customCaretPrev: Node;
	if (startTxtStrDeleted) {
		batch.insertText(txtStr.wedAnchor, data);
	} else if (txtStr.isVirtual()) {
		const rootVirtual = WEDLET.insertDatasFromDisplay(txtStr, batch, data).element;
		if (rootVirtual instanceof TxtElement && rootVirtual.isParaSibling) {
			rootVirtual.remove(); //on suppr ce para virtuel qui va être remplacé par une struct réelle.
		} else {
			customCaretParent = rootVirtual.parentElement;
			customCaretPrev = DOM.findPreviousSibling(rootVirtual, IS_EltWedlet);
		}
	} else {
		batch.insertText(XA.append(txtStr.wedAnchor, xmlOffset), data);
	}
	if (customCaretParent) {
		try {
			wedMgr.freezeFocus = true;
			batch.doBatch();
			if (!txtStr.isConnected) {
				//Reconstruction plus complexe de notre contexte virtuel (correction avec injections de balises...),
				//le widget virtuel a été remplacé, on doit le retrouver
				const filter = (n: Node): n is TxtStr => n instanceof TxtStr && n.getXmlText() == data
				const newTxtStr: TxtStr = customCaretPrev ? DOMSH.findFlatNextUncle(customCaretPrev, customCaretParent, filter) : DOMSH.findFlatNext(customCaretParent, customCaretParent, filter);
				if (newTxtStr) newTxtStr.txtRoot.selMgr.setCaretAtEnd(newTxtStr);
			} else {
				selMgr.setCaretAtEnd(txtStr);
			}
		} finally {
			wedMgr.freezeFocus = false;
		}
	} else {
		batch.doBatch();
	}
	if (ev.cancelable) {
		ev.preventDefault();
		if (ev instanceof InputEvent) {
			//Pas de onTypings sur une fin de IME composition.
			const onTypings = root.onTypings;
			if (onTypings) {
				let lang: string;
				DOMSH.findLogicalFlatParentOrSelf(txtStr, null, (n => (lang = n.getAttribute('lang')) != null) as INodeFilter<Element>);
				const text = txtStr.getXmlText();
				const beforeText = text.substr(0, xmlOffset);
				for (let i = 0; i < onTypings.length; i++) {
					if (onTypings[i].onTyping(ev, root, txtStr, lang, beforeText, xmlOffset, text)) break;
				}
			}
		}
	} else {
		//console.log("beforeInput not cancelable:::::", ev);
		//Event pas cancellable => insertCompositionText
		//On suppr la sel pour tuer l'input.
		selMgr.resetSel();
		setTimeout(() => {
			const [node, ofsset] = txtStr.getWebOffset(xmlOffset + data.length);
			selMgr.setSelCaret(node, ofsset);
		}, 4);
	}
}

/** https://www.w3.org/TR/input-events/#interface-InputEvent */
export const beforeInputHandler: Dict<(this: void, ev: InputEvent, root: TxtRoot) => void> = {
	insertText: function (this: void, ev: InputEvent, root: TxtRoot) {
		doInput(ev.data, ev, root);
	},

	insertReplacementText: function (this: void, ev: InputEvent, root: TxtRoot) {
		doInput(ev.dataTransfer.getData("text/plain") || "", ev, root);
	},

	/**
	 * Suppr de la sel avant le début d'une IME.
	 * https://w3c.github.io/input-events/#event-order-during-ime-composition
	 */
	deleteByComposition: function (this: void, ev: InputEvent, root: TxtRoot) {
		deleteFromSel(root, false, 'character');
	},

	// historyUndo: function (this: void, ev: InputEvent, root: TxtRoot) {
	// 	//Pile de undo/redo mal maitrisée par le navigateur.
	// 	//root.wedlet.wedMgr.docHolder.willUndo();
	// 	ev.preventDefault();
	// },
	//
	// historyRedo: function (this: void, ev: InputEvent, root: TxtRoot) {
	// 	//Pile de undo/redo mal maitrisée par le navigateur.
	// 	//root.wedlet.wedMgr.docHolder.willRedo();
	// 	ev.preventDefault();
	// }
};

export const keyPressHandler: Dict<(this: void, ev: KeyboardEvent, root: TxtRoot) => void> = {
	Enter: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopPropagation();
		ev.preventDefault();
		if (root.contentEditable !== "true") return;
		const selMgr = root.selMgr;
		const selType = selMgr.type;
		if (selType === 'None' || selType === 'Object') root.moveCaret(false);
		const range = selMgr.range; //ev.getTargetRanges()[0];
		const batch = root.wedlet.wedMgr.docHolder.newBatch(selMgr.getXaRange());
		if (!isStaticRangeCollapsed(range)) deleteWebRange(root, range, batch);
		const startTxtStr = findTxtStrParent(range.startContainer);
		if (startTxtStr) {
			const startPara = findTxtParaLikeFrom(startTxtStr);
			if (!startPara) {
				if (root instanceof TxtRootStr && root.getAttribute("aria-multiline") === "true") {
					//texte pur multi-ligne
					if (startTxtStr.isVirtual()) {
						WEDLET.insertDatasFromDisplay(startTxtStr, batch, '\n');
					} else {
						batch.insertText(XA.append(startTxtStr.wedAnchor, startTxtStr.getXmlOffset(range.startContainer, range.startOffset)), "\n");
					}
					batch.doBatch();
				}
				return;
			}
			if (startTxtStr.getXmlOffset(range.startContainer, range.startOffset) === 0 && startTxtStr.isFirstContentInPara()) {
				//Enter au début d'un para
				let prevPara: TxtElement;
				if (startPara.isEmpty() || (prevPara = findTxtEltPreviousSibling(startPara)) && prevPara.isEmpty()) {
					// notre para est vide ou le para précédent est vide => unwrap
					const ctn = findTxtEltParent(startPara);
					if (ctn && ctn.isUnwrappable() === "list") {//ol, ul, sl
						let lastPara: TxtElement = startPara;
						if (!(ctn instanceof TxtSL)) {//ul ou ol
							//On étend jusqu'au dernier para
							let nextParaLike = findTxtEltNextSibling(startPara);
							while (nextParaLike) {
								lastPara = nextParaLike;
								nextParaLike = findTxtEltNextSibling(nextParaLike);
							}
						}
						let prevPrevPara: TxtElement;
						if (prevPara && (prevPrevPara = findTxtEltPreviousSibling(prevPara)) && !prevPrevPara.isEmpty()) {
							//si para précédent est vide et que ce n'était pas le 1er (donc ne sera pas le seul après unwrap des suivants), on le vire
							if (prevPara.isVirtual()) {
								prevPara.remove();
							} else {
								batch.needAdjustForNextAdds().deleteSequence(prevPara.wedAnchor, 1).needAdjustForNextAdds();
							}
						}
						ctn.unwrapSeq("list", startPara, lastPara, batch, false);
						if (startPara.isVirtual()) startPara.remove();
						batch.setMeta(TxtSelMgr.MSGMETA_caretAtStart, true);
						batch.doBatch();
						return;
					}
				}
			}
			batch.needAdjustForNextAdds();
			const endPara = findTxtParaLikeFrom(range.endContainer);
			if (startPara === endPara) {
				const endTxtStr = findTxtStrParent(range.endContainer);
				let startMoveNode: TxtElement = endTxtStr;
				let startMoveOffset = endTxtStr.getXmlOffset(range.endContainer, range.endOffset);
				if (endTxtStr.isXmlEndOffset(range.endContainer, range.endOffset)) {
					//on tente de sortir des balises inline par la droite.
					const top = endTxtStr.getTopInlineParent('end');
					if (top) {
						startMoveNode = findTxtEltParent(top);
						startMoveOffset = top.getVirtualXaPart() + 1;
					}
				}
				const startTxtStr = findTxtStrParent(range.startContainer);
				if (startTxtStr.isXmlStartOffset(range.startContainer, range.startOffset) && DOM.findPreviousSibling(startTxtStr, IS_TxtElementReal) == null) {
					if (startTxtStr.isFirstContentInPara()) {
						//on était au début d'un paragraphe, on change de logique, on en insère un avant.
						batch.setSelAfter(buildXa(range.startContainer, range.startOffset));
						if (!startPara.addPreviousPara(batch)) return;
						batch.doBatch();
						return;
					}
				}
				const contentToMove = exportFragment(startMoveNode, startMoveOffset, endPara, findTxtEltLastChild(endPara).getVirtualXaPart() + 1);
				//console.log("contentToMove:::", contentToMove);
				if (contentToMove.length > 0) deleteWebRange(root, DOM.newRange(range.endContainer, range.endOffset, endPara, endPara.childNodes.length), batch);
				if (!startPara.addNextPara(batch, contentToMove)) {
					//retour charriot interdit, on annule tout.
					return;
				}
				if (batch.msgs.length === 0) {
					//cas du retour charriot en fin de ligne avec une ligne vide après !
					startPara.moveCaretOut(selMgr, false);
				} else {
					batch.setSelAfter(XA.append(XA.incrAtDepth(startPara.wedAnchor, -1, 1), 0));
				}
			} else {
				//rien à reprendre dans le nouveau paragraphe
				if (!startPara.addNextPara(batch)) return;
			}
			batch.doBatch();
		} else {
			console.trace("No startTxtStr!!!!");
		}
	},

	/** space : bloque la succession d'espaces. */
	' ': function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		if (root.contentEditable !== "true") return;
		if (root.selMgr.type === 'Caret') {
			const node = root.selMgr.focusNode;
			if (node instanceof Text) {
				const offset = root.selMgr.focusOffset;
				const txtStr = findTxtStrParent(node);
				if (!txtStr) return;
				if (!txtStr.model.keepSpaces) {
					//on bloque la succession d'espaces.
					if (node.nodeValue.charAt(offset - 1) === ' ') {
						//Un espce précède
						ev.preventDefault();
						ev.stopImmediatePropagation();
					} else if (node.nodeValue.charAt(offset) === ' ') {
						//Un espace suit
						ev.preventDefault();
						ev.stopImmediatePropagation();
						root.selMgr.setSelCaret(node, offset + 1);
					} else {
						if (txtStr.isXmlStartOffset(node, offset)) {
							//on est au début d'un noeud texte, on accepte si on n'est pas en début de para ou si le noeud précédent ne finit pas par espace.
							const prev = DOM.findPrevious(txtStr, findTxtParaLikeFrom(txtStr) || root, function (n: Node): n is TxtElement {
								return IS_TxtElement(n) && !n.isVirtual() && !(n instanceof InlWrap) && !(n instanceof TxtStr && n.isEmpty())
							});
							if (!prev || (prev instanceof TxtStr && DOM.txtEndsWithSp(prev.getXmlText()))) {
								ev.preventDefault();
								ev.stopImmediatePropagation();
							}
						} else if (txtStr.isXmlEndOffset(node, offset)) {
							//on est à la fin d'un noeud texte, si le noeud suivant commence par espace, on le suppr.
							const next = DOM.findNext(txtStr, findTxtParaLikeFrom(txtStr) || root, function (n: Node): n is TxtElement {
								return IS_TxtElement(n) && !n.isVirtual() && !(n instanceof InlWrap) && !(n instanceof TxtStr && n.isEmpty())
							});
							if (next instanceof TxtStr && DOM.txtStartsWithSp(next.getXmlText())) {
								root.wedMgr.docHolder.newBatch()
									.deleteSequence(XA.append(next.wedAnchor, 0), DOM.txtStartSpLen(next.getXmlText()))
									.insertText(XA.append(txtStr.wedAnchor, txtStr.getXmlTextLength()), ' ')
									.doBatch();
								ev.preventDefault();
								ev.stopImmediatePropagation();
							}
						}
					}
				}
			}
		}
	}
};

export const keyDownHandler: Dict<(this: void, ev: KeyboardEvent, root: TxtRoot) => void> = {
	ArrowLeft: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		root.moveCaret(true, ev.shiftKey ? 'extend' : 'move', ACTION.isAccelPressed(ev) ? 'word' : 'character');
	},
	ArrowRight: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopImmediatePropagation();
		ev.preventDefault();
		root.moveCaret(false, ev.shiftKey ? 'extend' : 'move', ACTION.isAccelPressed(ev) ? 'word' : 'character');
	},
	ArrowUp: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopImmediatePropagation();
		if (root.moveCaretV(true, ev.shiftKey ? 'extend' : 'move')) ev.preventDefault();
	},
	ArrowDown: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopImmediatePropagation();
		if (root.moveCaretV(false, ev.shiftKey ? 'extend' : 'move')) ev.preventDefault();
	},
	Backspace: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopImmediatePropagation();
		ev.preventDefault();
		if (root.contentEditable !== "true") return;
		if (ev.shiftKey) {
			//unwrap list ou div.
			unwrapContainerFromSel(null, root, false, false);
		} else {
			deleteFromSel(root, false, ACTION.isAccelPressed(ev) ? 'word' : 'character');
		}
	},
	Delete: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		ev.stopImmediatePropagation();
		ev.preventDefault();
		if (root.contentEditable !== "true") return;
		deleteFromSel(root, true, ACTION.isAccelPressed(ev) ? 'word' : 'character');
	},
	Insert: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		//Le mode d'édition insert est incompatible avec l'éditeur.
		ev.preventDefault();
		const table = DOM.findParentOrSelf(root.selMgr.commonAncestor, root, TXTTABLE.IS_TxtTable);
		if (table) {
			ev.stopImmediatePropagation();
			table.tableLayout.drawTableLayout();
		}
	},
	Enter: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		if (ACTION.isAccelPressed(ev)) {
			if (root.contentEditable !== "true") return;
			const cell = DOM.findParentOrSelf(root.selMgr.commonAncestor, root, TXTTABLE.IS_TxtCell);
			if (cell) {
				ev.stopImmediatePropagation();
				ev.preventDefault();
				//Insertion d'une ligne dans le tableau.
				const table = DOM.findParentOrSelf(cell, root, TXTTABLE.IS_TxtTable);
				const logicTable = table.tableLayout.logicTable;
				const batch = root.wedMgr.docHolder.newBatch();
				logicTable.insertRows(batch, logicTable.getLogicOffsets(cell).offsRow + 1, table.model.rowModels[0]);
				batch.doBatch();
			}
		}
	},
	Tab: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		if (root.selMgr.type === 'Object') return;
		const cell = DOM.findParentOrSelf(root.selMgr.commonAncestor, root, TXTTABLE.IS_TxtCell);
		if (cell) {
			const table = cell.parentNode.parentNode;

			function cellFilter(n: Node): n is TxtCell {return n instanceof TxtCell && n.parentNode.parentNode === table}

			const next = ev.shiftKey ? DOM.findPrevious(cell, table, cellFilter) : DOM.findNext(cell, table, cellFilter);
			if (next) {
				ev.stopImmediatePropagation();
				ev.preventDefault();
				root.selMgr.setSelection(next as any as ITxtElement);
			}
		}
	},

	/**
	 * Select All. Recodé car impl native chromium 81 est buggé si un objet a le focus (ObjectLeaf par ex.) : le container TxtRoot est traversé et tout le texte dans le wed est sélectionné.
	 */
	a: function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		if (ACTION.isAccelPressed(ev)) {
			root.selMgr.selectAll();
			ev.preventDefault();
		}
	},

	/** space : Espace inseccable (ctrl+shift+space) */
	' ': function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		if (!ev.shiftKey || !ACTION.isAccelPressed(ev) || root.contentEditable !== "true" || root.selMgr.type !== 'Caret') return;
		const node = root.selMgr.focusNode;
		if (node instanceof Text) {
			const offset = root.selMgr.focusOffset;
			const txtStr = findTxtStrParent(node);
			if (!txtStr) return;
			//Espace inseccable
			if (txtStr.isVirtual()) {
				WEDLET.insertDatasFromDisplay(txtStr, null, '\u00A0');
			} else {
				root.wedMgr.docHolder.newBatch().insertText(XA.append(txtStr.wedAnchor, txtStr.getXmlOffset(node, offset)), '\u00A0').doBatch();
			}
			ev.preventDefault();
			ev.stopImmediatePropagation();
		}
	},

	"F4": function (this: void, ev: KeyboardEvent, root: TxtRoot) {
		hintsMenu.execute(root, ev)
	}
}
keyDownHandler.A = keyDownHandler.a;

const DOUBLEPONCT = [':', ';', '?', '!'];
REG.reg.addToList("wed:text:onTyping", "nbspBeforeDoublePonctInFrench", 1, {
	label: "Espace insécable avant une double ponctuation (si langue française)",
	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
		if (!isFr(lang)) return false;
		if (DOUBLEPONCT.indexOf(ev.data) >= 0) {
			if (beforeXmlText.length === 0 || /\S$/.test(beforeXmlText) && !/.*(http|ftp)s?$/.test(beforeXmlText)) {
				//on insère un espace insécable.
				const xa = XA.freeze(txtStr.wedAnchor);
				root.wedMgr.docHolder.newBatch()
					.insertText(XA.append(xa, xmlOffset), '\u00A0')
					.setSelAfter(XA.append(xa, xmlOffset + 2))
					.doBatch();
			} else if (beforeXmlText.endsWith(' ')) {
				//on remplace l'espace.
				const xa = XA.freeze(txtStr.wedAnchor);
				root.wedMgr.docHolder.newBatch()
					.spliceSequence(XA.append(xa, xmlOffset - 1), 1, '\u00A0')
					.setSelAfter(XA.append(xa, xmlOffset + 1))
					.doBatch();
			}
			return true;
		}
		return false;
	}
} as IOnTyping);

REG.reg.addToList("wed:text:onTyping", "frenchQuote", 1, {
	label: "Guillemets « » (si langue française)",
	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
		if (!isFr(lang)) return false;
		if (ev.data === '"') {
			if (!beforeXmlText || /[\s({\[]$/.test(beforeXmlText)) {
				//caractères space avant ou début texte : on remplace le double quote par un guillemet gauche.
				root.wedMgr.docHolder.newBatch().spliceSequence(XA.append(txtStr.wedAnchor, xmlOffset), 1, '\u00AB\u00A0').doBatch();
			} else {
				//guillemet droit
				root.wedMgr.docHolder.newBatch().spliceSequence(XA.append(txtStr.wedAnchor, xmlOffset), 1, '\u00A0\u00BB').doBatch();
			}
			return true;
		}
		return false;
	}
} as IOnTyping);

// Désactivé car Pb pour adresse mail par exemple !
// REG.reg.addToList("wed:text:onTyping", "frenchInclusivePoint", 1, {
// 	label: "Point médian \u00B7 pour l'écriture inclusive (si langue française)",
// 	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
// 		if (isFr(lang)) return false;
// 		if (beforeXmlText.charAt(beforeXmlText.length - 1) === '.') {
// 			if (/[^\.\d\s]+/.test(ev.data) && /[^\d\s]\.$/.test(beforeXmlText)) {
// 				let xa = XA.append(txtStr.wedAnchor, xmlOffset - 1);
// 				root.wedMgr.docHolder.newBatch()
// 					.spliceSequence(xa, 1, '\u00B7')
// 					.setSelAfter(XA.setAtDepth(xa, -1, xmlOffset + ev.data.length))
// 					.doBatch();
// 				return true;
// 			}
// 		}
// 		return false;
// 	}
// } as IOnTyping);

function isFr(lang: string): boolean {return lang && lang.startsWith("fr")}

REG.reg.addToList("wed:text:onTyping", "ul", 1, {
	label: "* Liste",
	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
		if (beforeXmlText === '*' && ev.data === ' ' && !findTxtEltPreviousSibling(txtStr)) {
			const para = findTxtEltParent(txtStr);
			if (para && para.isParaSibling) {
				const ctn = findTxtEltOrRootParent(para);
				const models = ctn.txtWedModels;
				const model = (models ? models.find((m: TxtModel) => m instanceof TxtULModel) : null) as TxtULModel;
				if (model) {
					const doc = root.wedMgr.docHolder;
					const xa = para.wedAnchor;
					const paraJml = doc.getContent(xa);
					const ch = paraJml[1] as IJmlSet;
					const idx = ch.findIndex(c => typeof c === 'string');
					if (idx >= 0 && (ch[idx] as string).startsWith('* ')) ch[idx] = (ch[idx] as string).substring(2);
					doc.newBatch().spliceSequence(xa, 1, [model.newJml(), [model.itemModel.newJml(), paraJml]]).doBatch();
					return true;
				}
			}
		}
		return false;
	}
} as IOnTyping);

REG.reg.addToList("wed:text:onTyping", "ol", 1, {
	label: "1. Liste",
	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
		if (beforeXmlText === '1.' && ev.data === ' ' && !findTxtEltPreviousSibling(txtStr)) {
			const para = findTxtEltParent(txtStr);
			if (para && para.isParaSibling) {
				const ctn = findTxtEltOrRootParent(para);
				const models = ctn.txtWedModels;
				const model = (models ? models.find((m: TxtModel) => m instanceof TxtOLModel) : null) as TxtOLModel;
				if (model) {
					const doc = root.wedMgr.docHolder;
					const xa = para.wedAnchor;
					const paraJml = doc.getContent(xa);
					const ch = paraJml[1] as IJmlSet;
					const idx = ch.findIndex(c => typeof c === 'string');
					if (idx >= 0 && (ch[idx] as string).startsWith('1. ')) ch[idx] = (ch[idx] as string).substring(3);
					doc.newBatch().spliceSequence(xa, 1, [model.newJml(), [model.itemModel.newJml(), paraJml]]).doBatch();
					return true;
				}
			}
		}
		return false;
	}
} as IOnTyping);

REG.reg.addToList("wed:text:onTyping", "sl", 1, {
	label: "- Liste simple",
	onTyping(ev: InputEvent, root: TxtRoot, txtStr: TxtStr, lang: string, beforeXmlText: string, xmlOffset: number, fullXmlText: string): boolean {
		if (beforeXmlText === '-' && ev.data === ' ' && !findTxtEltPreviousSibling(txtStr)) {
			const para = findTxtEltParent(txtStr);
			if (para && para.isParaSibling) {
				const ctn = findTxtEltOrRootParent(para);
				const models = ctn.txtWedModels;
				const model = (models ? models.find((m: TxtModel) => m instanceof TxtSLModel) : null) as TxtSLModel;
				if (model) {
					const xa = para.wedAnchor;
					const paraJml = para.exportContent();
					const idx = paraJml.findIndex(c => typeof c === 'string');
					if (idx >= 0 && (paraJml[idx] as string).startsWith('- ')) paraJml[idx] = (paraJml[idx] as string).substring(2);
					root.wedMgr.docHolder.newBatch().spliceSequence(xa, 1, [model.newJml(), [model.memberModel.newJml(), paraJml]]).doBatch();
					return true;
				} else {
					//Pas de liste simple, fallback si liste multiple.
					const modelUL = (models ? models.find((m: TxtModel) => m instanceof TxtULModel) : null) as TxtULModel;
					if (modelUL) {
						const doc = root.wedMgr.docHolder;
						const xa = para.wedAnchor;
						const paraJml = doc.getContent(xa);
						const ch = paraJml[1] as IJmlSet;
						const idx = ch.findIndex(c => typeof c === 'string');
						if (idx >= 0 && (ch[idx] as string).startsWith('- ')) ch[idx] = (ch[idx] as string).substring(2);
						doc.newBatch().spliceSequence(xa, 1, [modelUL.newJml(), [modelUL.itemModel.newJml(), paraJml]]).doBatch();
						return true;
					}
				}
			}
		}
		return false;
	}
} as IOnTyping);

/** Constructeur de contenu par défaut pour tous les noeuds terminaux (Object, Leaf, Img...) à déclarer dans le wed txt générique. */
REG.reg.registerSvc('wedTxtUnknownLeaf', 1, function (this: TxtElement) {
	if (!this.shadowRoot) this.attachShadow(DOMSH.SHADOWDOM_INIT);
	DOM.addClass(this, "unknown");
	DOM.setTextContent(this.shadowRoot, this.model.nodeLabel);
} as ITxtRefresh);

